(ns client-portal.client.voice-insights.concept.views
  (:require [shared.core.components.components :as c]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [client-portal.client.anonymous.data :as anon.d]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.day-part-schedules.data :as dp]
            [client-portal.client.voice-insights.data :as vi.d]
            [client-portal.client.voice-insights.shared :as vi.shared]
            [client-portal.client.voice-insights.concept.data]
            [shared.core.util :as util]))

(def scoring-categories [{:category "marketing" :sort-order 1}
                         {:category "efficiency" :sort-order 2}
                         {:category "satisfaction" :sort-order 3}])

(defn concept-header
  []
  (let [concept-id- (re/subscribe [:concepts/current-concept-id-])
        conversations-count- (re/subscribe [:voice-insights/conversations-count-])
        conversations-duration- (re/subscribe [:voice-insights/conversations-avg-duration-])
        conversations-duration-over-time- (re/subscribe [:voice-insights/conversations-avg-duration-over-time-])
        crew-utterances-count- (re/subscribe [:voice-insights/crew-conversations-utterance-count-])
        customer-utterances-count- (re/subscribe [:voice-insights/user-conversations-utterance-count-])
        crew-utterances-count-over-time- (re/subscribe [:voice-insights/crew-conversations-utterance-count-over-time-])
        customer-utterances-count-over-time- (re/subscribe [:voice-insights/user-conversations-utterance-count-over-time-])
        concepts-list- (re/subscribe [:concepts/list-data-])
        locations-by-concept-id- (re/subscribe [:locations/by-current-concept-id-])
        concept-label (if (util/loading? @concepts-list-)
                        :loading
                        (some->> @concepts-list-
                                 (filter #(= (:id %) @concept-id-))
                                 (first)
                                 (anon.d/concept-label)))]
    [:div {:style {:display :flex
                   :flex-direction :column
                   :width "100%"
                   :padding 16}}
     [:div {:style {:display :flex
                    :align-items :center
                    :gap 12}}
      [:div {:style {:width 42
                     :height 42
                     :background-image (str "url(" (-> @concept-id- (concepts.d/concept-logo-url) (anon.d/logo-url)) ")")
                     :background-size :contain
                     :background-position :center
                     :background-repeat :no-repeat}}]
      [:div {:style {:display :flex
                     :flex-direction :column
                     :flex 1}}
       [:div {:style {:font-family style/roboto-font-family
                      :font-size 20
                      :line-height "32px"
                      :font-weight style/font-semi-bold-weight
                      :color (style/color "Text/primary")}}
        (if (util/loading? concept-label)
          [c/loader-rectangle {:width 380
                               :height 16
                               :style {:margin "8px 0"}}]
          concept-label)]
       [:div {:style {:font-family style/roboto-font-family
                      :font-size 15
                      :font-weight style/font-medium-weight
                      :line-height :normal
                      :color (style/alc-color "Text/secondary")}}
        (if (util/loading? @locations-by-concept-id-)
          [c/loader-rectangle {:width 240
                               :height 14
                               :style {:margin "2px 0"}}]
          (str (or (-> (count @locations-by-concept-id-) (util/readable-number)) 0)
               " "
               (util/pluralize "Location" (count @locations-by-concept-id-))))]]]
     [vi.shared/header-analytics {:conversations-count @conversations-count-
                                  :conversations-duration @conversations-duration-
                                  :conversations-duration-over-time @conversations-duration-over-time-
                                  :crew-utterances-count @crew-utterances-count-
                                  :customer-utterances-count @customer-utterances-count-
                                  :crew-utterances-count-over-time @crew-utterances-count-over-time-
                                  :customer-utterances-count-over-time @customer-utterances-count-over-time-}]]))

(defn concept-top-bottom-location
  [{:keys [location-id score score-color]}]
  (let [locations- (re/subscribe [:locations/by-current-concept-id-])
        location-id->label (when (util/not-loading? @locations-)
                             (into {} (map (fn [loc] [(str (:id loc)) (anon.d/location-label (:label loc))]) @locations-)))]
    [c/interactive
     :div {:style {:padding "0 16px"}
           :hover-style {:background-color (util/hex->rgba (style/alc-color "Background/page") 0.6)
                         :cursor :pointer}
           :on-click #(re/dispatch [:router/go-with-merged-query-params {:lid location-id}])}
     [:div {:style {:border-bottom (str "1px solid " (style/alc-color "Border/divider_secondary"))
                    :display :flex
                    :flex-direction :column
                    :gap 6
                    :padding "8px 0 12px"}}
      [:span {:style {:color (style/alc-color "Text/primary")
                      :font-size 12
                      :font-weight 400
                      :line-height 1.33}}
       (get location-id->label location-id)]
      [:div {:style {:display :flex
                     :align-items :center
                     :gap 8}}
       [:div {:style {:flex 1
                      :height 16}}
        [:div {:style {:width (str score "%")
                       :height "100%"
                       :background-color score-color
                       :border-radius 3}}]]
       [:span {:style {:color (style/alc-color "Text/secondary")
                       :font-size 12
                       :font-weight 400
                       :line-height 1.33}}
        score]]]]))

(defn concept-top-bottom-section
  [{:keys [title scores]}]
  [:div {:style {:width "100%"
                 :display :flex
                 :flex-direction :column}}
   [:div {:style {:margin "8px 16px"}}
    [:span {:style {:color (style/alc-color "Text/secondary")
                    :font-size 14
                    :font-weight 400
                    :line-height 1.43}}
     title]]
   (into [:div {:style {:display :flex
                        :flex-direction :column}}]
         (map (fn [score]
                [concept-top-bottom-location score])
              scores))])

(defn concept-top-bottom-locations
  [{:keys [category score-data]}]
  (let [top-locations (->> (sort-by :score > score-data) (take 3))
        bottom-locations (->> (sort-by :score < score-data) (take 3))]
    [alc/card {:title (str (util/capitalize-words category) " Scores by Location")
               :material-icon-type :pin-drop
               :style {:flex 1
                       :padding "16px 0"}
               :header-style {:padding "0 16px"}}
     (if (empty? score-data)
       [:div {:style {:width "100%"
                      :height 324
                      :margin-top 16
                      :display :flex
                      :align-items :center
                      :justify-content :center
                      :font-family style/roboto-font-family
                      :font-size 10
                      :font-weight style/font-medium-weight
                      :color (util/hex->rgba (style/color :black :base) 0.4)}}
        "Location information is not available."]
       [:div {:style {:width "100%"
                      :display :flex
                      :flex-direction :column}}
        [concept-top-bottom-section {:title "Top Locations"
                                     :scores (map #(assoc % :score-color (style/alc-color "chart/green"))
                                                  top-locations)}]
        [concept-top-bottom-section {:title "Bottom Locations"
                                     :scores (map #(assoc % :score-color (style/alc-color "chart/red"))
                                                  bottom-locations)}]])]))

(defn scores-loading []
  (into [:div {:class-name style/stack-on-mobile-class-name
               :style {:display :flex
                       :gap 8}}]
        (map (fn [_]
               [c/loader-rectangle {:height 340
                                    :style {:flex 1
                                            :min-height 340}}])
             (sort-by :sort-order scoring-categories))))

(defn concept-analytics
  [{:keys [concept-id]}]
  (let [scores-by-category- (re/subscribe [:voice-insights/scores-by-category-])
        scores-by-category-over-time- (re/subscribe [:voice-insights/scores-by-category-over-time-])
        scores-by-category-by-location- (re/subscribe [:voice-insights/scores-by-category-by-location-])
        day-part-schedules- (re/subscribe [:day-parts/by-current-concept-id-])
        conversation-tags-count- (re/subscribe [:voice-insights/conversation-tags-count-])
        selected-day-part- (re/subscribe [:voice-insights/selected-day-part-])
        satisfaction-name- (re/subscribe [:voice-insights/satisfaction-name-])]
    (fn []
      [:div {:style {:width "100%"
                     :flex 1
                     :display :flex
                     :justify-content :center}}
       [:div {:style {:width "100%"
                      :height "100%"
                      :padding "28px 16px"
                      :display :flex
                      :flex-direction :column
                      :gap 16}}
        [c/h1 {:style {:font-size 18
                       :font-family style/roboto-font-family
                       :font-weight 700
                       :line-height "24px"
                       :color (style/alc-color "Text/primary_header")}}
         "Insight Scores"]
        [vi.shared/day-parts-radio-buttons {:day-parts (when-not (util/loading? @day-part-schedules-)
                                                         (dp/concept-day-parts @day-part-schedules-))
                                            :selected-day-part @selected-day-part-
                                            :on-change #(do
                                                          (re/dispatch [:router/go-with-merged-query-params
                                                                        {:dp (:label %)}])
                                                          (re/dispatch [:voice-insights/select-day-part (:label %)]))}]
        (into [:div {:class-name style/stack-on-mobile-class-name
                     :style {:display :flex
                             :gap 8}}]
              (->> (sort-by :sort-order scoring-categories)
                   (map (fn [{:keys [category]}]
                          (let [avg-score (if (util/loading? @scores-by-category-)
                                            :loading
                                            (some->> (vi.d/matches-category category @scores-by-category-)
                                                     (first)
                                                     :score))
                                scores-over-time (if (util/loading? @scores-by-category-over-time-)
                                                   :loading
                                                   (vi.d/matches-category category @scores-by-category-over-time-))]
                            [vi.shared/concept-category-scores (cond-> {:category category
                                                                        :score avg-score
                                                                        :scores-over-time scores-over-time}

                                                                       (= category "marketing")
                                                                       (-> (assoc :icon-type :shopping-bag)
                                                                           (assoc :color (style/alc-color "Chart/tech_blue")))

                                                                       (= category "efficiency")
                                                                       (-> (assoc :icon-type :timer)
                                                                           (assoc :color (style/alc-color "Chart/orchid")))

                                                                       (= category "satisfaction")
                                                                       (-> (assoc :category-label @satisfaction-name-)
                                                                           (assoc :icon-type :emoji-emotions)
                                                                           (assoc :color (style/alc-color "Chart/turquoise"))))])))))
        (if (util/loading? @scores-by-category-by-location-)
          [scores-loading]
          (let [score-groups (group-by :category @scores-by-category-by-location-)]
            (into [:div {:class-name style/stack-on-mobile-class-name
                         :style {:display :flex
                                 :gap 8}}]
                  (map (fn [{:keys [category]}]
                         [concept-top-bottom-locations {:category category
                                                        :score-data (get score-groups category)}])
                       (sort-by :sort-order scoring-categories)))))
        [vi.shared/key-metrics @conversation-tags-count-
         {:location-metrics? true}]]])))

(defn dashboard-container [& contents]
  (into [:div {:style {:display :flex
                       :flex-direction :column
                       :width "100%"
                       :min-height "100%"}}]
        contents))

(defn dashboard-loading []
  [dashboard-container
   ;; TODO: Fix the styling of this
   #_[scores-loading]
   #_[vi.shared/key-metrics-loading]])

(defn loaded-dashboard [_]
  (let [concept-id- (re/subscribe [:concepts/current-concept-id-])]
    (re/create-class
      {:component-did-mount (fn [_]
                              (re/dispatch [:vi-concept/initialize]))
       :reagent-render
       (fn [args]
         (when @concept-id-
           [dashboard-container
            [concept-header args]
            [concept-analytics args]]))})))

(defn dashboard [_]
  (let [day-parts- (re/subscribe [:day-parts/by-current-concept-id-])]
    (fn [args]
      (if (util/loading? @day-parts-)
        [dashboard-loading]
        [loaded-dashboard args]))))
